import { memo, useEffect, useState } from "react";
import { useI18n } from "i18n";
import { useSelector } from "react-redux";
import { useAppDispatch } from "store/hooks";
import { StreamingContainer } from "modules/streaming";
import UserStatusContainer from "modules/userStatus/UserStatusContainer";
import { selectUser } from "modules/auth/redux/selectors";
import { updateEventUserPreference } from "services/firebaseService/userPreferences.firebase";
import EventBlacklistSubscriber from "modules/eventBlacklist/EventBlacklistSubscriber";
import { UserProfileCardContainer } from "modules/userProfileCard";
import EventUserPresenceTracker from "modules/eventUserPresence/EventUserPresenceTracker";
import {
  ensureSingleAppInstance,
  isMasterAppInstance,
} from "modules/ensureSingleAppInstance/EnsureSingleAppInstance";
import { selectIsInBroadcast } from "modules/broadcast/redux/selectors";
import CameraSettingsContainer from "modules/cameraSettings/CameraSettingsContainer";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import { SystemCheckNotifications } from "modules/systemCheck/components/SystemCheckNotifications";
import { selectIsChangeSpaceInProgress } from "modules/space/redux/selectors";
import { lazyLoad } from "helpers/lazyLoader";
import { JoinRoomEffects } from "modules/joinRoom/JoinRoomEffects";
import {
  selectCurrentEventId,
  selectIsEventManager,
} from "modules/event/selectors";
import { RollbackDetector } from "modules/version";
import { PageLoader } from "@remo-co/ui-core/src/components/PageLoader";
import { usePollsSubscription } from "modules/polls/hooks/usePollsSubscription";
import { getVar } from "config";
import logger from "logging/logger";
import MapViewContextProvider from "modules/svgMapView/templateParts/MapViewContext";
import { TimerPanelContainer } from "modules/timer/component/TimerPanelContainer";
import { makeSelectIsUserInTutorial } from "modules/userStatus/redux/selectors";
import useCompanySettingsActions from "modules/companySettings/hooks/useCompanySettingsActions";
import { RightPanelContainer } from "modules/rightPanel";
import WelcomeMessage from "modules/onboarding/guest/WelcomeMessage";
import UserProblemSubscriber from "modules/userProblemReporter/UserProblemSubscriber";
import EventAgendaContextProvider from "modules/eventAgenda/context/EventAgendaContext";
import { useDaily } from "@daily-co/daily-react";
import { PAGES, usePageTracking } from "modules/tracking";
import { PresentContentDisplay } from "modules/eventEmbedContent/components/PresentContentDisplay";
import { EventActivityStatusListener } from "modules/eventActivityStatus/components/EventActivityStatusListener";
import { InviteTableListener } from "modules/joinFloor/component/InviteTableListener";
import { AutoFloorSwitcher } from "modules/joinFloor/component/AutoFloorSwitcher";
import { NetworkingRecommendations } from "modules/networkingRecommendations/hooks/NetworkingRecommendations";
import { ShuffleAttendeesContainer } from "modules/shuffleAttendees/components/ShuffleAttendeesContainer";
import { initTheaterFirebase, unsubscribeTheaterFirebase } from "./firebase";
import { selectCurrentTheaterId, selectTheaterCompany } from "./selectors";
import TheaterDataLoader from "./TheaterDataLoader";
import { resetTheater } from "./theaterSlice";
import { useStyles } from "./TheaterView.styles";
import { ConversationModeWrapper } from "./components/ConversationModeWrapper";

const LobbyContainer = lazyLoad(() => import("../lobby/LobbyContainer"));

const TheaterView = () => {
  const dispatch = useAppDispatch();
  const eventId = useSelector(selectCurrentEventId);
  const isSpaceChangeInProgress = useSelector(selectIsChangeSpaceInProgress);
  const { t } = useI18n(["theater"]);
  const theaterId = useSelector(selectCurrentTheaterId);
  const theaterCompany = useSelector(selectTheaterCompany);
  const isInBroadcast = useSelector(selectIsInBroadcast);
  const styles = useStyles({ isInBroadcast: Boolean(isInBroadcast) });
  const user = useSelector(selectUser);
  const isMeInTutorial = useSelector(
    makeSelectIsUserInTutorial(user?.id ?? ""),
  );
  const isEventManager = useSelector(selectIsEventManager);
  const { subscribeToCompanySettings, unsubscribeFromCompanySettings } =
    useCompanySettingsActions();

  const [isLoading, setIsLoading] = useState(true);
  const { addErrorNotification } = useNotificationActions();

  const daily = useDaily();

  usePageTracking(PAGES.EVENT_FLOOR, {
    metadata: {
      eventId,
    },
  });
  usePollsSubscription({ eventId });

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (theaterCompany) {
      logger.info("[TheaterView] theater mount.");
      initTheaterFirebase(t);
      subscribeToCompanySettings(theaterCompany);
      // Track theaterId
      updateEventUserPreference({ theaterId });

      return () => {
        logger.info("[TheaterView] theater unmount.");
        if (daily) {
          daily.leave();
        }
        if (isMasterAppInstance) {
          unsubscribeTheaterFirebase();
          dispatch(resetTheater());
          unsubscribeFromCompanySettings(theaterCompany);
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theaterCompany, theaterId, daily]);

  if (!user) {
    return null;
  }

  if (isEventManager && !LobbyContainer) {
    addErrorNotification({ message: t("loading.lobby.error") });
  }

  if (!isInBroadcast && !ShuffleAttendeesContainer) {
    addErrorNotification({ message: t("loading.shuffle.error") });
  }

  if (theaterId) {
    return (
      <MapViewContextProvider>
        {(isLoading || isSpaceChangeInProgress) && (
          <div className="svg-map-loader loading-div">
            <PageLoader message={t("theater:loading.space")} />
          </div>
        )}
        <div id="host-onboarding-ctn" />
        <EventAgendaContextProvider isInEventSettings={false}>
          {!isInBroadcast && <ConversationModeWrapper isLoading={isLoading} />}
          {eventId && <PresentContentDisplay eventId={eventId} />}
          {isEventManager && LobbyContainer && <LobbyContainer />}
          <div
            data-testid="streaming-wrapper"
            style={{ height: "100vh" }}
            className={styles.root}
          >
            {!isMeInTutorial && <TimerPanelContainer />}
            {!isLoading && <StreamingContainer />}
          </div>
          <UserProfileCardContainer />
          {!isLoading && <UserStatusContainer />}
          <WelcomeMessage />
          {!isInBroadcast && (
            <div className={styles.rightPanelContainer}>
              <RightPanelContainer />
            </div>
          )}
          <EventBlacklistSubscriber />
          <EventUserPresenceTracker />
          <InviteTableListener />
          <AutoFloorSwitcher />
          <JoinRoomEffects />
          <NetworkingRecommendations />
          <UserProblemSubscriber />
          <CameraSettingsContainer />
          <SystemCheckNotifications />
        </EventAgendaContextProvider>
        {!getVar("REACT_APP_PREVIEW_HOST") && <RollbackDetector />}
        <TheaterDataLoader setIsLoading={setIsLoading} />
        <EventActivityStatusListener />
      </MapViewContextProvider>
    );
  }

  return null;
};

export default ensureSingleAppInstance(memo(TheaterView));
